import { createContext, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import logging from 'src/services/logging';
import { db } from 'src/services/firebase-service';

type CycleContextType = {
  activeRound: number;
  isLoading: boolean;
  roundOneStatus: number;
  roundTwoStatus: number;
  roundThreeStatus: number;
  roundFourStatus: number;
  season: number;
  setSeason: (value: number) => void;
  setActiveRound: (value: number) => void;
  setRoundOneStatus: (value: number) => void;
  setRoundTwoStatus: (value: number) => void;
  setRoundThreeStatus: (value: number) => void;
  setRoundFourStatus: (value: number) => void;
};

export const CycleContext = createContext<CycleContextType>({
  activeRound: 1,
  isLoading: false,
  roundOneStatus: 0,
  roundTwoStatus: 0,
  roundThreeStatus: 0,
  roundFourStatus: 0,
  season: 0,
  setSeason: () => {},
  setActiveRound: () => {},
  setRoundOneStatus: () => {},
  setRoundTwoStatus: () => {},
  setRoundThreeStatus: () => {},
  setRoundFourStatus: () => {},
});

interface Props {
  children: React.ReactNode;
}

export const CycleProvider = ({ children }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeRound, _setActiveRound] = useState<number>(1);
  const [roundOneStatus, _setRoundOneStatus] = useState<number>(0);
  const [roundTwoStatus, _setRoundTwoStatus] = useState<number>(0);
  const [roundThreeStatus, _setRoundThreeStatus] = useState<number>(0);
  const [roundFourStatus, _setRoundFourStatus] = useState<number>(0);
  const [season, _setSeason] = useState<number>(0);

  useEffect(() => {
    const getCurrentCycle = async () => {
      try {
        setIsLoading(true);
        const q = query(
          collection(db, 'hokage'),
          where('isCurrentSeason', '==', true)
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs[0];
        logging.info(data.data(), 'CycleContext:getCurrentCycle');
        _setSeason(data.data().season);
        _setActiveRound(data.data().currentRound);
        _setRoundOneStatus(data.data().roundOneStatus);
        _setRoundTwoStatus(data.data().roundTwoStatus);
        _setRoundThreeStatus(data.data().roundThreeStatus);
        _setRoundFourStatus(data.data().roundFourStatus);
      } catch (error) {
        logging.error(error, 'CycleContext:getSeason');
      } finally {
        setIsLoading(false);
      }
    };
    getCurrentCycle();
  }, [season]);

  const setSeason = (value: number) => {
    // this will be controlled by global season selector
    // so users can look at historical data
    _setSeason(value);
  };

  const setActiveRound = (value: number) => {
    // this will be controlled by global season selector
    // so users can look at historical data
    _setActiveRound(value);
  };

  const setRoundOneStatus = (value: number) => {
    _setRoundOneStatus(value);
  };

  const setRoundTwoStatus = (value: number) => {
    _setRoundTwoStatus(value);
  };

  const setRoundThreeStatus = (value: number) => {
    _setRoundThreeStatus(value);
  };

  const setRoundFourStatus = (value: number) => {
    _setRoundFourStatus(value);
  };

  const value = {
    activeRound,
    roundOneStatus,
    roundTwoStatus,
    roundThreeStatus,
    roundFourStatus,
    isLoading,
    season,
    setSeason,
    setActiveRound,
    setRoundOneStatus,
    setRoundTwoStatus,
    setRoundThreeStatus,
    setRoundFourStatus,
  };

  return (
    <CycleContext.Provider value={value}>{children}</CycleContext.Provider>
  );
};
